
import { Question } from '../types';

export const qur101AbrogationQuestions: Question[] = [
  {
    id: 'qur101',
    text: 'What is naskh (abrogation) linguistically?',
    type: 'single',
    options: [
      { id: 'a', text: 'To transcribe or copy something', isCorrect: true },
      { id: 'b', text: 'To remove or delete', isCorrect: false },
      { id: 'c', text: 'To explain or clarify', isCorrect: false },
      { id: 'd', text: 'To suppress or conceal', isCorrect: false }
    ],
    explanation: 'Linguistically (lughatan), naskh (abrogation) means "to transcribe or copy something." This root meaning relates to the idea of writing one thing in place of another.',
    topic: 'qur101'
  },
  {
    id: 'qur102',
    text: 'What is the technical (iṣṭilāḥan) definition of naskh?',
    type: 'single',
    options: [
      { id: 'a', text: 'The lifting of a legal ruling by another legal ruling', isCorrect: true },
      { id: 'b', text: 'The explanation of an ambiguous verse', isCorrect: false },
      { id: 'c', text: 'The reconciliation of apparently contradictory verses', isCorrect: false },
      { id: 'd', text: 'The specification of a general command', isCorrect: false }
    ],
    explanation: 'Technically (iṣṭilāḥan), naskh means "the lifting of a legal ruling by another legal ruling." This occurs when a later revelation supersedes an earlier one, making the earlier ruling no longer valid after the point of abrogation.',
    topic: 'qur101'
  },
  {
    id: 'qur103',
    text: 'What types of sources can abrogate Qur\'ānic rulings?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Verses from the Qur\'ān', isCorrect: true },
      { id: 'b', text: 'Mutawātir ḥadīth (of absolute authenticity)', isCorrect: true },
      { id: 'c', text: 'Scholarly consensus (ijmā\')', isCorrect: false },
      { id: 'd', text: 'Logical reasoning (qiyās)', isCorrect: false }
    ],
    explanation: 'According to Islamic scholarship, abrogation of Qur\'ānic rulings can only occur through: verses from the Qur\'ān itself or mutawātir ḥadīth (narrations of absolute authenticity that require no investigation about their authenticity). Scholarly consensus, logical reasoning, or non-mutawātir ḥadīth cannot abrogate Qur\'ānic rulings.',
    topic: 'qur101'
  },
  {
    id: 'qur104',
    text: 'Which verses in the Qur\'ān explicitly mention the concept of abrogation?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Sūrah Baqarah verse mentioning bringing a better verse if one is abrogated', isCorrect: true },
      { id: 'b', text: 'Sūrah Naḥl verse mentioning substitution of verses', isCorrect: true },
      { id: 'c', text: 'Sūrah Ra\'d verse about Allāh erasing what He wills', isCorrect: false },
      { id: 'd', text: 'Sūrah Qāf verse about creation of new revelations', isCorrect: false }
    ],
    explanation: 'Two verses in the Qur\'ān explicitly mention the concept of abrogation: a verse in Sūrah Baqarah stating that Allāh would send a better verse if one were to be abrogated, and a verse in Sūrah Naḥl mentioning the substitution of revelations. These verses establish abrogation as a divine practice within revelation.',
    topic: 'qur101'
  },
  {
    id: 'qur105',
    text: 'What is the first category of naskh according to scholars?',
    type: 'single',
    options: [
      { id: 'a', text: 'Where both the ruling and recitation are abrogated', isCorrect: true },
      { id: 'b', text: 'Where the ruling is abrogated but the recitation remains', isCorrect: false },
      { id: 'c', text: 'Where the recitation is abrogated but the ruling remains', isCorrect: false },
      { id: 'd', text: 'Where neither is abrogated but the application changes', isCorrect: false }
    ],
    explanation: 'The first category of naskh according to scholars is where both the ruling and recitation are abrogated. This means that verses are no longer in the final edition of the Qur\'ān and their rulings are no longer valid. These were revealed and recited during the Prophet\'s lifetime but were later abrogated completely.',
    topic: 'qur101'
  },
  {
    id: 'qur106',
    text: 'What is an example of the first category of naskh (both ruling and recitation abrogated)?',
    type: 'single',
    options: [
      { id: 'a', text: 'Aisha RA\'s narration about five feedings making a milk sibling', isCorrect: true },
      { id: 'b', text: 'The verse about inheritance in Sūrat al-Baqarah', isCorrect: false },
      { id: 'c', text: 'The verses about alcohol prohibition', isCorrect: false },
      { id: 'd', text: 'The verse about the change of qiblah', isCorrect: false }
    ],
    explanation: 'An example of the first category of naskh (both ruling and recitation abrogated) is Aisha RA\'s narration that there used to be a verse stating that it took five feedings to establish a milk sibling relationship. This verse is no longer in the Qur\'ān, though its ruling influenced the Shāfi\'ī school, which requires five feedings, while the Ḥanafis say one feeding is sufficient.',
    topic: 'qur101'
  },
  {
    id: 'qur107',
    text: 'What is the second category of naskh according to scholars?',
    type: 'single',
    options: [
      { id: 'a', text: 'Where the recitation is abrogated but the ruling remains', isCorrect: true },
      { id: 'b', text: 'Where both the ruling and recitation are abrogated', isCorrect: false },
      { id: 'c', text: 'Where the ruling is abrogated but the recitation remains', isCorrect: false },
      { id: 'd', text: 'Where the application changes but neither ruling nor recitation is abrogated', isCorrect: false }
    ],
    explanation: 'The second category of naskh according to scholars is where the recitation is abrogated but the ruling remains. This means that certain verses are not in the final edition of the Qur\'ān, but their rulings are still applied in Islamic law. An example would be verses about specific punishments that are no longer recited but whose legal effects continue.',
    topic: 'qur101'
  },
  {
    id: 'qur108',
    text: 'What is the third category of naskh according to scholars?',
    type: 'single',
    options: [
      { id: 'a', text: 'Where the ruling is abrogated but the recitation remains', isCorrect: true },
      { id: 'b', text: 'Where the recitation is abrogated but the ruling remains', isCorrect: false },
      { id: 'c', text: 'Where both the ruling and recitation are abrogated', isCorrect: false },
      { id: 'd', text: 'Where application changes without abrogation', isCorrect: false }
    ],
    explanation: 'The third category of naskh according to scholars is where the ruling is abrogated but the recitation remains. This means that the verse is still in the Qur\'ān but we don\'t act upon its ruling anymore because it has been superseded by later revelation. This is the most common and least controversial form of abrogation.',
    topic: 'qur101'
  },
  {
    id: 'qur109',
    text: 'What example illustrates the stages of prohibition of alcohol in the Qur\'ān?',
    type: 'multiple',
    options: [
      { id: 'a', text: '2:219 - Intoxicants have benefits and sin, but sin outweighs benefits', isCorrect: true },
      { id: 'b', text: '4:43 - Prohibition of praying while intoxicated', isCorrect: true },
      { id: 'c', text: '5:90 - Complete prohibition of intoxicants', isCorrect: true },
      { id: 'd', text: '7:33 - Initial mention of alcohol as shameful', isCorrect: false }
    ],
    explanation: 'The stages of prohibition of alcohol in the Qur\'ān illustrate a gradual approach to abrogation: (1) 2:219 mentions that intoxicants have benefits and sin, but the sin outweighs the benefits; (2) 4:43 prohibits praying while intoxicated (partial prohibition); and (3) 5:90 establishes complete prohibition, describing intoxicants as filth to be avoided entirely. Each stage built upon and eventually replaced the previous ruling.',
    topic: 'qur101'
  },
  {
    id: 'qur110',
    text: 'Which statement is most accurate about the scholars of tafsir\'s view on the categories of naskh?',
    type: 'single',
    options: [
      { id: 'a', text: 'Only the third category (ruling abrogated, recitation remains) technically holds', isCorrect: true },
      { id: 'b', text: 'All three categories are equally valid and common', isCorrect: false },
      { id: 'c', text: 'The first category (both abrogated) is the most important', isCorrect: false },
      { id: 'd', text: 'The second category (recitation abrogated, ruling remains) is rejected', isCorrect: false }
    ],
    explanation: 'According to many scholars of tafsir, while three categories of naskh are theoretically proposed, only the third category (where the ruling is abrogated but the recitation remains in the Qur\'ān) technically holds as true abrogation in the strictest sense. The other categories involve verses that were ultimately not included in the final Qur\'ānic compilation rather than being formally "abrogated" in the technical sense.',
    topic: 'qur101'
  }
];
